// src/pages/BusinessCheckoutPage.js
import React, { useState, useEffect } from 'react';
import {
    CurrencyDollarIcon,
    HandThumbUpIcon,
    XCircleIcon,
    UserCircleIcon,
    ArrowPathIcon, ReceiptPercentIcon,
} from '@heroicons/react/24/outline';

// Mock customer data - In a real app, you'd fetch this from your backend based on the client ID
const mockCustomersDB = {
    'cli_001': { id: 'cli_001', name: 'Thabo Mokoena', email: 'thabo.m@example.com', currentPoints: 450, program: 'LoyalCoffee' },
    'cli_002': { id: 'cli_002', name: 'Jessica Nkosi', email: 'jess.n@example.com', currentPoints: 200, program: 'FashionRewards' },
    // Add more mock customers as needed for testing
};

// Mock loyalty program rules
const mockLoyaltyPrograms = {
    'LoyalCoffee': {
        pointsPerRand: 0.1, // 0.1 points per R1 spent
    },
    'FashionRewards': {
        pointsPerRand: 0.05, // 0.05 points per R1 spent
    },
};

const BusinessCheckoutPage = ({ scannedClientId="cli_001", onPointsProcessed, onReset }) => {
    const [client, setClient] = useState(mockCustomersDB["cli_001"]);
    const [amountSpent, setAmountSpent] = useState('');
    const [purchaseDescription, setPurchaseDescription] = useState('');
    const [calculatedPoints, setCalculatedPoints] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        // Fetch client data when scannedClientId changes
        if (scannedClientId) {
            // In a real app, this would be an API call:
            // fetch(`/api/customers/${scannedClientId}`)
            //   .then(res => res.json())
            //   .then(data => setClient(data));
            const foundClient = mockCustomersDB[scannedClientId];
            if (foundClient) {
                setClient(mockCustomersDB["cli_001"]);
            } else {
                setMessage({ type: 'error', text: 'Client not found.' });
                setClient(null);
            }
        } else {
            setClient(null);
        }
    }, [scannedClientId]);

    // Calculate points whenever amountSpent or client changes
    useEffect(() => {
        if (client && amountSpent && mockLoyaltyPrograms[client.program]) {
            const parsedAmount = parseFloat(amountSpent);
            if (!isNaN(parsedAmount) && parsedAmount > 0) {
                const programRules = mockLoyaltyPrograms[client.program];
                const points = Math.floor(parsedAmount * programRules.pointsPerRand);
                setCalculatedPoints(points);
            } else {
                setCalculatedPoints(0);
            }
        } else {
            setCalculatedPoints(0);
        }
    }, [amountSpent, client]);

    const handleProcessPoints = async () => {
        if (!client) {
            setMessage({ type: 'error', text: 'No client selected.' });
            return;
        }
        if (calculatedPoints <= 0) {
            setMessage({ type: 'error', text: 'Invalid amount or zero points.' });
            return;
        }

        setIsProcessing(true);
        setMessage({ type: '', text: '' });

        try {
            // Simulate API call to update client points and transaction
            // await fetch('/api/transactions', {
            //   method: 'POST',
            //   headers: { 'Content-Type': 'application/json' },
            //   body: JSON.stringify({
            //     clientId: client.id,
            //     points: calculatedPoints,
            //     amount: amountSpent,
            //     description: purchaseDescription,
            //   }),
            // });

            // Mock success
            // In a real app, the backend would update the client's points
            // and create a transaction record.
            setMessage({
                type: 'success',
                text: `Successfully awarded ${calculatedPoints} points to ${client.name}.`,
            });
            // For this mock, we're not updating the client's points directly.
            if (onPointsProcessed) {
                onPointsProcessed(calculatedPoints); // Notify the parent component
            }

        } catch (error) {
            console.error('Error processing transaction:', error);
            setMessage({ type: 'error', text: 'Transaction failed. Please try again.' });
        } finally {
            setIsProcessing(false);
        }
    };

    const handleReset = () => {
        setAmountSpent('');
        setPurchaseDescription('');
        setCalculatedPoints(0);
        setMessage({ type: '', text: '' });
        if (onReset) {
            onReset(); // Notify parent to clear the scanned client
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-500 to-indigo-600 p-4 sm:p-6 lg:p-8">
            {console.log(mockCustomersDB["cli_001"])}
            <div className="max-w-xl mx-auto bg-white rounded-xl shadow-2xl p-6 sm:p-8 space-y-6 animate-fade-in">
                <div className="text-center">
                    <CurrencyDollarIcon className="h-16 w-16 text-indigo-600 mx-auto mb-3" />
                    <h2 className="text-2xl font-extrabold text-gray-900">
                        Checkout & Award Points
                    </h2>
                    <p className="mt-1 text-md text-gray-600">
                        Enter transaction details to calculate and award points.
                    </p>
                </div>

                {/* Client Info */}
                {client && (
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-3">
                        <div className="flex items-center space-x-4">
                            <UserCircleIcon className="h-10 w-10 text-green-500" />
                            <div>
                                <h3 className="text-lg font-bold text-gray-800">{client.name}</h3>
                                <p className="text-gray-600 text-sm">Loyalty Program: {client.program}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Messages */}
                {message.text && (
                    <div className={`p-4 rounded-md ${message.type === 'success' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-red-100 border-red-400 text-red-700'} border flex items-center`}>
                        {message.type === 'success' ? (
                            <HandThumbUpIcon className="h-5 w-5 mr-2" />
                        ) : (
                            <XCircleIcon className="h-5 w-5 mr-2" />
                        )}
                        <p className="text-sm font-medium">{message.text}</p>
                    </div>
                )}

                {/* Transaction Input */}
                {client && (
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="amount-spent" className="block text-sm font-medium text-gray-700">
                                Amount Spent (R)
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </div>
                                <input
                                    type="number"
                                    id="amount-spent"
                                    name="amount-spent"
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    placeholder="e.g., 150.00"
                                    value={amountSpent}
                                    onChange={(e) => setAmountSpent(e.target.value)}
                                    min="0"
                                    step="0.01"
                                    disabled={isProcessing}
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="purchase-description" className="block text-sm font-medium text-gray-700">
                                Purchase Description
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <ReceiptPercentIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </div>
                                <input
                                    type="text"
                                    id="purchase-description"
                                    name="purchase-description"
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    placeholder="e.g., Coffee and sandwich"
                                    value={purchaseDescription}
                                    onChange={(e) => setPurchaseDescription(e.target.value)}
                                    disabled={isProcessing}
                                />
                            </div>
                        </div>

                        <div className="bg-blue-50 p-4 rounded-md text-blue-800 text-center text-sm font-medium">
                            Calculated Points: <span className="text-xl font-bold">{calculatedPoints}</span>
                        </div>

                        <div className="flex space-x-3">
                            <button
                                onClick={handleProcessPoints}
                                disabled={isProcessing || !client || calculatedPoints <= 0}
                                className="flex-1 inline-flex justify-center py-3 px-4 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isProcessing ? (
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    <HandThumbUpIcon className="-ml-1 mr-2 h-5 w-5" />
                                )}
                                Award Points
                            </button>
                            <button
                                onClick={handleReset}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                            >
                                <ArrowPathIcon className="-ml-1 mr-2 h-5 w-5" /> Reset
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BusinessCheckoutPage;